import React, { useEffect, useState } from "react";
import { Button, Checkbox, CheckboxProps, Col, Form, Input, message, Modal, Popconfirm, Row, Select, SelectProps, Spin } from "antd";
import { useCompany } from "../../contexts/CompanyContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useStore } from "../../contexts/StoreContext";
import { EditOutlined } from "@ant-design/icons";
import { mutate } from "swr";
import { usePos } from "../../contexts/PosContext";
import { useSecureRequest } from "../../hooks/useSecureRequest";

interface IformValues {
  name: string,
  alias: string,
  address: string,
  storehouse_id: number
  storehouse_uid: string
  storehouse_name: string
}
type TPosRequest = IformValues & {
  id?: number,
  company_id: number,
}
type TRequestData = {
  company: string | null,
  module: string,
  pos: TPosRequest,
  uidPos?: string
}
function PosModal(){
  const {pos,updatePos} = usePos()
  const {store} = useStore()
  const {company} = useCompany()
  const {config} = useConfig()
  const [form] = Form.useForm<IformValues>()
  const { Option } = Select
  const [loading, setLoading] = useState(false)
  const [edit, setEdit] = useState(false)
  const [messageApi, contextHolder] = message.useMessage();
  const currentData = !!pos.currentItem?.uid
  const { securePost } = useSecureRequest()

  useEffect(()=>{
    if(pos.currentItem){
      form.setFieldsValue(pos.currentItem)
    }else{
      form.resetFields()
    }
  },[pos.currentItem])

  const handlePos = async (values:IformValues) => {
    const foundByAlias = pos.list.some(el => el.alias === values.alias)
    if(foundByAlias && !edit){
      return fail("Ya existe un registro con el identificador " + values.alias)
    }
    setLoading(true)
    let dataRequest:TRequestData = {
      company:company.current.uid,
      module: config.module,
      pos: {
        ...values,
        company_id: company.current.id
      }
    }
    let url = "/sale/pos/create-pos"
    if(edit && currentData){
      dataRequest.pos.id = pos.currentItem.id
      dataRequest.uidPos = pos.currentItem.uid
      url = "/sale/pos/update-pos"
    }
    console.log("dataRequest",dataRequest)
    const storeResponse = await securePost(url,dataRequest)
    if(storeResponse.status === 200){
      mutate(key => Array.isArray(key) && typeof key[0] === 'string' && key[0].startsWith('/sale/pos/get-pos'), undefined, { revalidate: true })
      updatePos({showModal:false,currentItem:undefined})
      form.setFieldsValue({})
      setEdit(false)
      success(edit && currentData ? 'Registro actualizado con éxito' : 'Registro creado con éxito')
    }else{
      fail("Por favor intente nuevamente")
    }
    setLoading(false)
  }
  const success = (content: string = 'Registro creado con éxito') => {
    messageApi.open({
      type: 'success',
      content,
    });
  };
  const fail = (content:string) => {
    messageApi.open({
      type: 'error',
      content,
    });
  };

  const onClose = () => {
    updatePos({showModal:false,currentItem:undefined})
    setEdit(false)
    form.resetFields()
    form.setFieldsValue({})
  }
  const footer = [
    <Popconfirm
      key="confirm"
      title="¿Estás seguro de realizar esta acción?"
      okButtonProps={{htmlType:"submit",form:"pos-form"}}
      okText="Sí"
      cancelText="No"
    >
      <Button type="primary" disabled={!edit} htmlType="button">Actualizar</Button>
    </Popconfirm>,
    <Button 
      key="cancel" 
      type="default" 
      htmlType="button" 
      onClick={onClose}>Cancelar
    </Button>
  ]
  if(!currentData){
    footer[0] = <Popconfirm
      key="confirm"
      title="¿Estás seguro de realizar esta acción?"
      okButtonProps={{htmlType:"submit",form:"pos-form"}}
      okText="Sí"
      cancelText="No"
    >
      <Button type="primary" htmlType="button">Crear</Button>
    </Popconfirm>
  }
  const onChange: CheckboxProps['onChange'] = (e) => {
    setEdit(e.target.checked);
  };
  const onChangeStorehouse: SelectProps['onChange'] = (value) => {
    const storehouse = store.list?.find(el => el.id === value)
    form.setFieldsValue({
      storehouse_uid: storehouse?.uid,
      storehouse_name: storehouse?.name
    })
  }
  return (
    <Modal
      title={<div style={{ textAlign: "center" }}>Datos de punto de venta</div>}
      open={pos.showModal}
      maskClosable={false}
      confirmLoading={loading}
      onCancel={onClose}
      className="modal-responsive-md"
      destroyOnHidden
      footer={footer}
      >
      <Spin size="large" tip="Procesando..." fullscreen spinning={loading} />
      {contextHolder}
      <Form
        form={form}
        name="pos-form"
        layout="vertical"
        scrollToFirstError={{ block: 'end', focus: true }}
        style={{ maxWidth: '800px', margin: '0 auto'}}
        onFinish={handlePos}>
        <Form.Item hidden name="storehouse_uid"><Input type="hidden" /></Form.Item>
        <Form.Item hidden name="storehouse_name"><Input type="hidden" /></Form.Item>
        {currentData && <Row gutter={[10,10]} style={{marginBottom:20}}>
          <Col span={24}>
            <Checkbox checked={edit} onChange={onChange}>
              <EditOutlined style={{ marginRight: 6 }} />
              Editar
            </Checkbox>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item
                label="Nombre"
                name={`name`}
                rules={[{ required: true, message: 'Ingrese un nombre valido'}]}
              >
                <Input type="text" placeholder="Punto de venta principal" disabled={!edit} minLength={3} maxLength={30}/>
              </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item
                label="Identificador"
                name={`alias`}
                rules={[{ required: true, message: 'Ingrese un nombre valido'}]}
              >
                <Input type="text" placeholder="PVDPPL" disabled minLength={3} maxLength={6}/>
              </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item
                label="Dirección"
                name={`address`}
                rules={[{ required: true, message: 'Ingrese una dirección valida'}]}
              >
                <Input type="text" disabled={!edit} minLength={3} maxLength={30}/>
              </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item
                label="Almacen"
                name={`storehouse_id`}
                rules={[{ required: true, message: 'No ha seleccionado un almacén'}]}
              >
                <Select placeholder="Seleccione un almacén" disabled>
                  {
                    store.list.map(el => <Option key={el.uid} value={el.id}>{el.name}</Option>)
                  }
                </Select>
              </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item
              label="Estado"
              name="is_active"
              valuePropName="checked"
            >
              <Checkbox disabled={!edit}>Habilitado</Checkbox>
            </Form.Item>
          </Col>
        </Row>}
        {!currentData && <Row gutter={[10,10]} style={{marginBottom:20}}>
          <Col sm={12} xs={24}>
            <Form.Item
                label="Nombre"
                name={`name`}
                rules={[{ required: true, message: 'Ingrese un nombre valido'}]}
              >
                <Input type="text" minLength={3} maxLength={30}/>
              </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item
                label="Identificador"
                name={`alias`}
                rules={[{ required: true, message: 'Ingrese un nombre valido'}]}
              >
                <Input type="text" minLength={3} maxLength={6}/>
              </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item
                label="Dirección"
                name={`address`}
                rules={[{ required: true, message: 'Ingrese una dirección valida'}]}
              >
                <Input type="text" minLength={3} maxLength={30}/>
              </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item
                label="Almacen"
                name={`storehouse_id`}
                rules={[{ required: true, message: 'No ha seleccionado un almacén'}]}
              >
                <Select placeholder="Seleccione un almacén" onChange={onChangeStorehouse}>
                  {
                    store.list?.map(el => <Option key={el.uid} value={el.id}>{el.name}</Option>)
                  }
                </Select>
              </Form.Item>
          </Col>
        </Row>}
      </Form>
    </Modal>
  )

}

export default PosModal